import {
  detectExplicitLanguageSwitch,
  languageFromFirstTypedMessage,
} from './infer-language-preference.js'

export type ReplyLanguage = 'nb' | 'nn' | 'en'

function fromDetected(code: string | null, fallback: ReplyLanguage): ReplyLanguage {
  if (code?.startsWith('nn')) return 'nn'
  if (code?.startsWith('en')) return 'en'
  if (code?.startsWith('nb')) return 'nb'
  return fallback
}

/** A clear sentence switches immediately. A short answer stays on the current language. */
export function customerReplyLanguage(text: string, current: ReplyLanguage): ReplyLanguage {
  const explicit = detectExplicitLanguageSwitch(text)
  if (explicit) return fromDetected(explicit, current)
  return fromDetected(languageFromFirstTypedMessage(text), current)
}

/**
 * The Live model sees this. The stored visitor line does not.
 * A clear sentence names the reply language. A short answer does not.
 */
export function liveTypedModelText(visible: string): string {
  const explicit = detectExplicitLanguageSwitch(visible)
  const detected = explicit ?? languageFromFirstTypedMessage(visible)
  if (detected?.startsWith('en')) {
    return `[REPLY LANGUAGE: English. Reply only in English.]\n${visible}`
  }
  if (detected?.startsWith('nb') || detected?.startsWith('nn')) {
    return `[REPLY LANGUAGE: Norwegian. Reply only in Norwegian.]\n${visible}`
  }
  return visible
}

/** Research copy never chooses the reply language. */
export function languageAfterResearch(visitor: ReplyLanguage, _researchText: string): ReplyLanguage {
  return visitor
}

/**
 * The browser chooses the opener before the first typed line.
 * An explicit plate choice wins over this. The email TLD does not.
 */
export function openerLocaleForPlate(
  _email: string | null | undefined,
  browserLanguage: string | null | undefined
): ReplyLanguage | null {
  const browser = (browserLanguage ?? '').toLowerCase()
  if (browser.startsWith('nn')) return 'nn'
  if (browser.startsWith('nb') || browser.startsWith('no')) return 'nb'
  return null
}

/**
 * Plate chrome for the address being typed.
 * A .no address is Norwegian. The spoken opener still follows the browser.
 */
export function plateLocaleForWorkEmail(
  email: string | null | undefined,
  browserLanguage: string | null | undefined,
): ReplyLanguage | null {
  const domain = (email?.split('@')[1] ?? '').toLowerCase().trim()
  if (domain.endsWith('.no')) return 'nb'
  return openerLocaleForPlate(email, browserLanguage)
}

/** First line establishes the language. Later lines may switch it. Research is ignored. */
export function intakeLanguageForTurn(text: string, current: ReplyLanguage | null): ReplyLanguage {
  const seeded = fromDetected(languageFromFirstTypedMessage(text), current ?? 'nb')
  return languageAfterResearch(customerReplyLanguage(text, seeded), '')
}

/** UI, PDF, and email only have nb and en. Nynorsk uses the Norwegian UI. */
export function writtenUiLocale(text: string, current: 'en' | 'nb'): 'en' | 'nb' {
  return customerReplyLanguage(text, current) === 'en' ? 'en' : 'nb'
}

/** Walk typed lines in order. A short line keeps the language it arrived in. */
export function uiLocaleFromWrittenTurns(
  turns: readonly string[],
  current: 'en' | 'nb' = 'en',
): 'en' | 'nb' {
  let locale = current
  for (const turn of turns) locale = writtenUiLocale(turn, locale)
  return locale
}

/**
 * A transcript chooses the report language.
 * No typed line yet: the request locale stands (the plate, before they write).
 */
export function reportLocaleFromWriting(
  userLines: readonly string[],
  projectionLocale: 'en' | 'nb',
  requested: 'en' | 'nb',
): 'en' | 'nb' {
  if (userLines.length === 0) return requested
  return uiLocaleFromWrittenTurns(userLines, projectionLocale)
}

/**
 * Report and email locale for the whole conversation.
 * Typed lines decide. When the visitor's lines after the last clear line are
 * too short to detect, the reply that answered them shows the language the
 * chat ended in. Used by the report/email path only, not by text-chat replies.
 */
export function reportLocaleFromConversation(
  turns: ReadonlyArray<{ role: string; content: string }>,
  projectionLocale: 'en' | 'nb',
  requested: 'en' | 'nb',
): 'en' | 'nb' {
  const userLines = turns.filter(turn => turn.role === 'user' && turn.content.trim())
  if (userLines.length === 0) return requested
  let locale: 'en' | 'nb' = projectionLocale
  let lastClearUser = -1
  turns.forEach((turn, index) => {
    if (turn.role !== 'user' || !turn.content.trim()) return
    const next = writtenUiLocale(turn.content, locale)
    if (customerReplyLanguageIsClear(turn.content)) lastClearUser = index
    locale = next
  })
  const trailingUnclearUser = turns
    .slice(lastClearUser + 1)
    .some(turn => turn.role === 'user' && turn.content.trim())
  if (!trailingUnclearUser) return locale
  for (let index = turns.length - 1; index > lastClearUser; index--) {
    const turn = turns[index]
    if (!turn || turn.role !== 'assistant') continue
    if (!customerReplyLanguageIsClear(turn.content)) continue
    return writtenUiLocale(turn.content, locale)
  }
  return locale
}

function customerReplyLanguageIsClear(text: string): boolean {
  return Boolean(detectExplicitLanguageSwitch(text) ?? languageFromFirstTypedMessage(text))
}
